/**
 * /empresas — Cadastro do EMISSOR: razão social, endereço, dados fiscais
 * (SIREN/SIRET/APE/TVA/capital social), email institucional e logo. Esses
 * campos aparecem no cabeçalho/rodapé da fatura (F-invoice-fiscal). Dados
 * bancários (IBAN/BIC) têm página própria — link na dica.
 *
 * F1 do redesenho (CEO 2026-09-23): tela no vocabulário de formulário
 * (`components/form/FormKit`), organizada nas mesmas TRÊS seções que o
 * operador já lê no eDSP — identificação, contacto, informações legais —
 * com a barra de ações grudada no rodapé. Com duas empresas (ES e BS) cada
 * uma vira um acordeão: a principal abre, a outra fica fechada.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import {
  Accordion,
  Field,
  FormGrid,
  FormPage,
  FormSection,
  Hint,
  PageActions,
} from "../components/form/FormKit.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

interface Draft {
  name: string;
  legalName: string;
  email: string;
  vatNumber: string;
  fiscalId: string;
  siren: string;
  siret: string;
  codeApe: string;
  capitalSocial: string;
  addrStreet: string;
  addrPostal: string;
  addrCity: string;
  addrCountry: string;
  /** F-company-logo — storage_key do logo (vazio = sem logo). */
  logoStorageKey: string;
  logoMime: string;
}

function draftFromCompany(c: Record<string, unknown>): Draft {
  const addr = (c.address ?? {}) as Record<string, unknown>;
  const s = (v: unknown): string => (v == null ? "" : String(v));
  return {
    name: s(c.name),
    legalName: s(c.legalName),
    email: s(c.email),
    vatNumber: s(c.vatNumber),
    fiscalId: s(c.fiscalId),
    siren: s(c.siren),
    siret: s(c.siret),
    codeApe: s(c.codeApe),
    capitalSocial: s(c.capitalSocial),
    addrStreet: s(addr.street),
    addrPostal: s(addr.postalCode),
    addrCity: s(addr.city),
    addrCountry: s(addr.country),
    logoStorageKey: s(c.logoStorageKey),
    logoMime: s(c.logoMime),
  };
}

export default function Empresas() {
  const { user } = useAuth();
  const tr = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const utils = trpc.useUtils();

  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [uploadingLogoId, setUploadingLogoId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ companyId: number; ok: boolean; msg: string } | null>(
    null,
  );

  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  useEffect(() => {
    if (!companiesQuery.data) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const c of companiesQuery.data!) {
        if (next[c.id] === undefined) next[c.id] = draftFromCompany(c as Record<string, unknown>);
      }
      return next;
    });
  }, [companiesQuery.data]);

  const updateMutation = trpc.companies.update.useMutation({
    onSuccess: (_, vars) => {
      setFeedback({ companyId: vars.companyId, ok: true, msg: tr("companies.dataSaved") });
      utils.companies.list.invalidate();
    },
    onError: (e, vars) => setFeedback({ companyId: vars.companyId, ok: false, msg: e.message }),
    onSettled: () => setSavingId(null),
  });

  function set(companyId: number, patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [companyId]: { ...prev[companyId]!, ...patch } }));
  }

  function save(companyId: number) {
    const d = drafts[companyId];
    if (!d) return;
    setSavingId(companyId);
    setFeedback(null);
    const t = (v: string) => (v.trim() === "" ? undefined : v.trim());
    updateMutation.mutate({
      companyId,
      patch: {
        name: d.name.trim(),
        legalName: t(d.legalName),
        email: t(d.email),
        vatNumber: t(d.vatNumber),
        fiscalId: t(d.fiscalId),
        siren: t(d.siren),
        siret: t(d.siret),
        codeApe: t(d.codeApe),
        capitalSocial: d.capitalSocial.trim() === "" ? null : Number(d.capitalSocial),
        address: {
          street: t(d.addrStreet),
          postalCode: t(d.addrPostal),
          city: t(d.addrCity),
          country: t(d.addrCountry),
        },
        // F-company-logo — vazio limpa (null); senão grava a storage_key.
        logoStorageKey: d.logoStorageKey.trim() === "" ? null : d.logoStorageKey.trim(),
        logoMime: d.logoMime.trim() === "" ? null : d.logoMime.trim(),
      },
    });
  }

  // F-company-logo — sobe o arquivo via POST /uploads (multipart) e guarda a
  // storage_key retornada no draft. O Salvar persiste em companies.logo_storage_key.
  async function uploadLogo(companyId: number, file: File) {
    setUploadingLogoId(companyId);
    setFeedback(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("companyId", String(companyId));
      fd.append("ownerId", String(companyId));
      fd.append("ownerKind", "company_logo");
      const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
      if (!resp.ok) {
        const msg = await resp.text();
        setFeedback({
          companyId,
          ok: false,
          msg: `${tr("companies.uploadFailed")} (${resp.status}): ${msg}`,
        });
        return;
      }
      const data = (await resp.json()) as { storageKey: string; mimeType: string | null };
      set(companyId, { logoStorageKey: data.storageKey, logoMime: data.mimeType ?? "" });
      setFeedback({ companyId, ok: true, msg: tr("companies.logoUploaded") });
    } catch (err) {
      setFeedback({
        companyId,
        ok: false,
        msg: `${tr("companies.uploadFailed")}: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setUploadingLogoId(null);
    }
  }

  if (!user) return null;

  const uma = accessibleCompanies.length === 1;

  return (
    <ErpShell>
      <FormPage
        icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18M5 21V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16M14 9h4a1 1 0 0 1 1 1v11" />
            <path d="M8 8h3M8 12h3M8 16h3" />
          </svg>
        }
        title={tr("companies.title")}
        subtitle={tr("companies.pageSubtitle")}
      >
        <Hint>
          {tr("companies.descPre")}
          <b>{tr("companies.descEmphasis")}</b>
          {tr("companies.descMid")}
          <Link href="/contas-bancarias" className="underline">
            {tr("companies.bankAccountsLink")}
          </Link>
          .
        </Hint>

        {companiesQuery.isLoading && (
          <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
            {tr("companies.loading")}
          </p>
        )}
        {accessibleCompanies.length === 0 && !companiesQuery.isLoading && (
          <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
            {tr("companies.noCompanies")}
          </p>
        )}

        {accessibleCompanies.map((c, i) => {
          const d = drafts[c.id];
          if (!d) return null;
          const fb = feedback?.companyId === c.id ? feedback : null;
          const corpo = (
            <>
              <FormSection title={tr("companies.secIdentity")}>
                <FormGrid>
                  <Field label={tr("companies.tradeName")} required>
                    <input
                      className="fin"
                      value={d.name}
                      onChange={(e) => set(c.id, { name: e.target.value })}
                    />
                  </Field>
                  <Field label={tr("companies.legalName")}>
                    <input
                      className="fin"
                      value={d.legalName}
                      onChange={(e) => set(c.id, { legalName: e.target.value })}
                    />
                  </Field>
                </FormGrid>
                <FormGrid>
                  <Field label={tr("companies.addrStreet")}>
                    <input
                      className="fin"
                      value={d.addrStreet}
                      onChange={(e) => set(c.id, { addrStreet: e.target.value })}
                    />
                  </Field>
                  <Field label={tr("companies.addrPostal")}>
                    <input
                      className="fin"
                      value={d.addrPostal}
                      onChange={(e) => set(c.id, { addrPostal: e.target.value })}
                    />
                  </Field>
                  <Field label={tr("companies.addrCity")}>
                    <input
                      className="fin"
                      value={d.addrCity}
                      onChange={(e) => set(c.id, { addrCity: e.target.value })}
                    />
                  </Field>
                  <Field label={tr("companies.addrCountry")}>
                    <input
                      className="fin"
                      value={d.addrCountry}
                      onChange={(e) => set(c.id, { addrCountry: e.target.value })}
                    />
                  </Field>
                </FormGrid>
              </FormSection>

              <FormSection title={tr("companies.secContact")} desc={tr("companies.secContactDesc")}>
                <Field label={tr("companies.institutionalEmail")}>
                  <input
                    className="fin"
                    type="email"
                    value={d.email}
                    onChange={(e) => set(c.id, { email: e.target.value })}
                  />
                </Field>
              </FormSection>

              <FormSection title={tr("companies.secLegal")} desc={tr("companies.secLegalDesc")}>
                <FormGrid>
                  <Field label={tr("companies.vatNumber")}>
                    <input
                      className="fin"
                      value={d.vatNumber}
                      onChange={(e) => set(c.id, { vatNumber: e.target.value })}
                      placeholder="FR41815253711"
                    />
                  </Field>
                  <Field label={tr("companies.siren")}>
                    <input
                      className="fin"
                      value={d.siren}
                      onChange={(e) => set(c.id, { siren: e.target.value })}
                      placeholder="815253711"
                    />
                  </Field>
                  <Field label={tr("companies.siret")}>
                    <input
                      className="fin"
                      value={d.siret}
                      onChange={(e) => set(c.id, { siret: e.target.value })}
                      placeholder="81525371100012"
                    />
                  </Field>
                  <Field label={tr("companies.codeApe")}>
                    <input
                      className="fin"
                      value={d.codeApe}
                      onChange={(e) => set(c.id, { codeApe: e.target.value })}
                      placeholder="45.20A"
                    />
                  </Field>
                  <Field label={tr("companies.capitalSocial")}>
                    <input
                      className="fin"
                      type="number"
                      step="0.01"
                      min={0}
                      value={d.capitalSocial}
                      onChange={(e) => set(c.id, { capitalSocial: e.target.value })}
                      placeholder="10000"
                    />
                  </Field>
                  <Field label={tr("companies.fiscalIdOther")}>
                    <input
                      className="fin"
                      value={d.fiscalId}
                      onChange={(e) => set(c.id, { fiscalId: e.target.value })}
                    />
                  </Field>
                </FormGrid>
              </FormSection>

              {/* F-company-logo — logo do cabeçalho do PDF da fatura. */}
              <FormSection title={tr("companies.logoLabel")}>
                <div className="flex flex-wrap items-center gap-4">
                  {d.logoStorageKey ? (
                    <img
                      src={`/uploads/${d.logoStorageKey}`}
                      alt={tr("companies.logoAlt")}
                      className="h-14 max-w-[180px] rounded bg-white object-contain p-1"
                      style={{ border: "1px solid var(--border)" }}
                    />
                  ) : (
                    <div
                      className="flex h-14 w-[180px] items-center justify-center rounded text-xs"
                      style={{
                        border: "1px dashed var(--border-strong)",
                        color: "var(--text-faint)",
                      }}
                    >
                      {tr("companies.noLogo")}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      disabled={uploadingLogoId === c.id}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadLogo(c.id, file);
                        e.target.value = "";
                      }}
                      className="text-xs file:mr-2 file:cursor-pointer file:rounded file:border-0 file:px-2 file:py-1"
                      style={{ color: "var(--text-muted)" }}
                    />
                    {uploadingLogoId === c.id && (
                      <span className="fhelp">{tr("companies.uploading")}</span>
                    )}
                    {d.logoStorageKey && uploadingLogoId !== c.id && (
                      <button
                        type="button"
                        onClick={() => set(c.id, { logoStorageKey: "", logoMime: "" })}
                        className="text-left text-[11px]"
                        style={{ color: "var(--danger-500)" }}
                      >
                        {tr("companies.removeLogo")}
                      </button>
                    )}
                    <span className="fhelp">
                      {tr("companies.logoHintPre")}
                      <b>{tr("companies.save")}</b>
                      {tr("companies.logoHintSuf")}
                    </span>
                  </div>
                </div>
              </FormSection>

              <PageActions
                saveLabel={savingId === c.id ? tr("companies.saving") : tr("companies.save")}
                onSave={() => save(c.id)}
                saving={savingId === c.id}
                message={fb ? { tone: fb.ok ? "ok" : "err", text: fb.msg } : null}
              />
            </>
          );

          // Uma empresa só: sem acordeão — o operador não deve ter de abrir
          // nada para chegar ao próprio cadastro.
          if (uma) return <div key={c.id}>{corpo}</div>;
          return (
            <Accordion
              key={c.id}
              title={c.name}
              desc={`#${c.id} · ${c.countryCode}`}
              defaultOpen={i === 0}
            >
              {corpo}
            </Accordion>
          );
        })}
      </FormPage>
    </ErpShell>
  );
}
